import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { clientLocationsService, clientsService, contractsService, cnpjService } from '../services/api';
import { fmtCNPJ, maskCNPJ } from '../utils/formatters';
import { montarGrupos, idsDeMembros, grupoCasaBusca, casaBusca, textoDeCobranca, membroInativo, soDigitos, GClient } from '../utils/grupoEconomico';
import { useEstadoDaCarga, EstadoDaLista, opcaoAtualForaDaLista, mensagemDoErroDeCarga, campo, textos } from '../design';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { RedividirDoGrupo } from './RedividirRateio';
import { RateioDoGrupo } from './RateioDoGrupoExpandido';
import CascaDeModal from './shared/CascaDeModal';
import MaisAcoes from './shared/MaisAcoes';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import { CascaDaLista, CartaoDoTopo, partesDaSubLinha } from './shared/lista/PecasDaLista';

interface ClientLocation {
  id: number;
  clientId: number;
  code: string;
  name: string;
  cnpj: string;
  companyName?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  emailFinancial?: string;
  phone?: string;
  dueDay?: number;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive: boolean;
  notes?: string;
}

interface Client {
  id: number;
  name?: string; // Nome de exibição
  tradeName: string;
  companyName: string;
  cnpj: string;
  emailFinancial?: string;
  phone?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dueDay?: number;
  status: string;
  /** Cliente separado ligado a uma matriz (caso b do grupo econômico). */
  parentClientId?: number | null;
  /** O local de que este cliente é o espelho (criado pelo local). */
  locationId?: number | null;
}

interface ContractItem {
  productId: number;
  name: string;
  description?: string;
  type: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  locationCode?: string;
  locationId?: number;
}

interface Contract {
  id: number;
  clientId: number;
  items: ContractItem[];
  mrr: number;
  status: string;
}

const Icon = ({ name, className }: { name: string; className?: string }) => {
  const icons: any = {
    building: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' /></svg>,
    mapPin: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' /></svg>,
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    chevronDown: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' /></svg>,
    chevronRight: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    dollar: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
  };
  return icons[name] || null;
};

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


// Classes do modal de empresa no escopo do MÓDULO (ver BankAccountsTab: nada
// declarado dentro do componente que precise ficar estável entre renders).
const CAMPO = `w-full px-3 py-2.5 ${campo} rounded-lg text-sm focus:outline-none`;
const ROTULO = 'block text-sm text-slate-400 mb-1.5';
const SECAO = 'text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3';

/**
 * ⛔ O CNPJ DO LOCAL JÁ É DE UM CLIENTE (09/10/2026). Salvar um local com CNPJ manda o backend sincronizar o cliente
 * daquele CNPJ (client-locations.service, `create`/`update` → `syncLocationToClient`): ACHANDO o cliente, ele REESCREVE
 * nome, razão social, endereço, telefone e status com os do formulário, e o e-mail financeiro e o dia de vencimento com
 * os do formulário OU OS DA MATRIZ, e só então liga o `parentClientId`. A fatura da empresa passaria a ir para o e-mail
 * da matriz, calada. Por isso o caminho do local recusa CNPJ que já é de cliente cadastrado e oferece o vínculo, que
 * grava só o `parentClientId`. Quem já é o espelho DESTE local (o cliente que ele criou, `locationId`) não conta.
 */
export function clienteComOCnpj<T extends { id: number; cnpj?: string | null; locationId?: number | null }>(
  cnpj: string | null | undefined,
  clientes: T[],
  localId?: number | null,
): T | null {
  const d = soDigitos(cnpj);
  if (d.length !== 14) return null;
  return clientes.find((c) => soDigitos(c.cnpj) === d && !(localId != null && c.locationId === localId)) || null;
}

interface Props {
  onBack?: () => void;
  /** Matriz do grupo a abrir ao chegar (botão "Abrir no Grupos Econômicos" da ficha da assinatura). */
  abrirGrupo?: number | null;
  /** Abre o cadastro CANÔNICO do cliente (o modal da Lista, no App). É a porta única do cadastro a partir desta tela. */
  onEditarCliente?: (cliente: { id: number }) => void;
  /** O cadastro canônico está aberto. Ao fechar, a tela relê: o que ele gravou tem de aparecer aqui. */
  cadastroAberto?: boolean;
}

/** Quanto a carga dos grupos espera antes de virar erro (a requisição segue no fundo; a tela não fica refém dela). */
export const PRAZO_DA_CARGA_DOS_GRUPOS_MS = 60000;
function comPrazo<T>(p: Promise<T>, ms = PRAZO_DA_CARGA_DOS_GRUPOS_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(textos.servidorNaoRespondeuNoPrazo(Math.round(ms / 1000)))), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

const ClientLocationsManager: React.FC<Props> = ({ onBack, abrirGrupo, onEditarCliente, cadastroAberto }) => {
  const confirm = useConfirm();
  const aviso = useAviso();
  const [clients, setClients] = useState<Client[]>([]);
  const [locations, setLocations] = useState<ClientLocation[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  // Redividir grava o rateio (rota ADMIN): o botão só aparece para admin.
  const [redividindo, setRedividindo] = useState<number | null>(null);
  const [versaoDoRateio, setVersaoDoRateio] = useState(0);
  // Grupo que acabou de gravar a primeira divisão: o "Aplicar as cotas" abre sozinho quando o rateio novo for lido.
  const [aplicarAoLer, setAplicarAoLer] = useState<number | null>(null);
  const ehAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin'; } catch { return false; } })();
  const [search, setSearch] = useState('');
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  // Grupos em que o usuário pediu para ver as empresas inativas (fechado por padrão).
  const [inativosVisiveis, setInativosVisiveis] = useState<Set<number>>(new Set());
  const toggleInativos = (clientId: number) => setInativosVisiveis(prev => {
    const next = new Set(prev);
    if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
    return next;
  });
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ClientLocation | null>(null);
  const [form, setForm] = useState<Partial<ClientLocation>>({});
  const [saving, setSaving] = useState(false);
  const [searchingCnpj, setSearchingCnpj] = useState(false);
  // O "+ Vincular empresa" tem DOIS caminhos (09/10/2026), e o primeiro passo é escolher qual. Até aqui só existia o
  // local: vincular um cliente que já existe (a FISCALMAX no grupo da CONTROLSUL) só se fazia por SQL.
  //  - `cliente`: cliente já cadastrado vira filial (grava `parentClientId`); fatura própria, cadastro intacto.
  //  - `local`: endereço/CNPJ novo dentro da conta da matriz (client_locations).
  const [passo, setPasso] = useState<'escolher' | 'cliente' | 'local'>('local');
  const [filialId, setFilialId] = useState<number | ''>('');
  const [filtroFilial, setFiltroFilial] = useState('');
  const carga = useEstadoDaCarga();

  useEffect(() => {
    loadData();
  }, []);

  // O cadastro canônico fechou (salvo ou não): relê, para o grupo mostrar o cadastro que está no servidor.
  const cadastroEstavaAberto = useRef(false);
  useEffect(() => {
    if (cadastroEstavaAberto.current && !cadastroAberto) loadData();
    cadastroEstavaAberto.current = !!cadastroAberto;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadastroAberto]);

  // Chegando da ficha da assinatura: abre o grupo pedido e o traz à vista.
  useEffect(() => {
    if (loading || !abrirGrupo) return;
    setExpandedClients(prev => new Set(prev).add(abrirGrupo));
    setTimeout(() => document.getElementById(`grupo-${abrirGrupo}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [loading, abrirGrupo]);

  // A carga é estado (09/10/2026): o passo de vincular cliente lista os clientes daqui, e lista que não carregou não
  // pode dizer "nenhum cliente para vincular". O `rodar` não lança; com erro numa recarga, a lista que estava fica.
  //
  // A RECARGA NÃO APAGA A TELA (10/10/2026). Fechar o cadastro canônico relê o grupo (ver `cadastroAberto`), e a
  // releitura punha `loading` em true, o que trocava a tela INTEIRA pelo spinner até as três listas voltarem. Em
  // produção o Heitor fechou o cadastro da CONTROLSUL e a tela ficou só no spinner: requisição que não volta (a
  // instância do axios não tem timeout) deixava o `loading` preso para sempre. Agora o spinner de tela cheia é só da
  // PRIMEIRA carga, sem dados; recarga com dados mantém a tela ("Atualizando…"), e a carga tem prazo: passou dele,
  // vira erro com "Tentar de novo".
  const loadData = async () => {
    setLoading(true);
    await carga.rodar(async () => {
      const [clientsData, locationsData, contractsData] = await comPrazo(Promise.all([
        clientsService.getAll(),
        clientLocationsService.getAll(),
        contractsService.getAll(),
      ]));
      setClients(clientsData);
      setLocations(locationsData);
      setContracts(contractsData.map((c: any) => ({
        ...c,
        items: typeof c.items === 'string' ? JSON.parse(c.items || '[]') : (c.items || [])
      })));
    });
    setLoading(false);
  };

  const toggleClient = (clientId: number) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const openAddLocation = (client: Client) => {
    setSelectedClient(client);
    setEditingLocation(null);
    setForm({ clientId: client.id, code: '', name: '', cnpj: '', isActive: true });
    setPasso('escolher');
    setFilialId('');
    setFiltroFilial('');
    setShowModal(true);
  };

  const openEditLocation = (location: ClientLocation) => {
    setSelectedClient(clients.find(c => c.id === location.clientId) || null);
    setEditingLocation(location);
    setForm({ ...location });
    setPasso('local');
    setShowModal(true);
  };

  const saveLocation = async () => {
    if (!form.code || !form.name) {
      aviso('Preencha o código e o nome do local', 'warning');
      return;
    }
    // Na edição, só o CNPJ TROCADO passa pela trava: o local que já existe segue sincronizando o espelho dele.
    const cnpjNovo = !editingLocation || soDigitos(form.cnpj) !== soDigitos(editingLocation.cnpj);
    const dono = cnpjNovo ? clienteComOCnpj(form.cnpj, clients, editingLocation?.id) : null;
    if (dono) {
      aviso(`Este CNPJ já é do cliente ${nomeDoCliente(dono)}. Salvar como local reescreveria o cadastro dele: vincule como filial.`, 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingLocation) {
        await clientLocationsService.update(editingLocation.id, form);
      } else {
        await clientLocationsService.create(form);
      }
      await loadData();
      setShowModal(false);
    } catch (e) {
      console.error('Erro ao salvar:', e);
      aviso('Erro ao salvar o local', 'error');
    }
    setSaving(false);
  };

  const deleteLocation = async (location: ClientLocation) => {
    // useConfirm do HUB no lugar do confirm nativo (trava a página e não diz a consequência).
    if (!(await confirm({
      title: `Remover o local ${location.code} - ${location.name}?`,
      danger: true,
      confirmLabel: 'Remover',
      consequence: 'O local sai do cadastro do grupo. Não pode ser desfeito.',
    }))) return;
    try {
      await clientLocationsService.delete(location.id);
      await loadData();
    } catch (e) {
      console.error('Erro ao excluir:', e);
      aviso('Erro ao remover o local', 'error');
    }
  };

  const handleCnpjChange = async (value: string) => {
    const maskedValue = maskCNPJ(value);
    setForm({ ...form, cnpj: maskedValue });

    // Buscar dados quando CNPJ tiver 14 dígitos
    const cleanCnpj = maskedValue.replace(/\D/g, '');
    if (cleanCnpj.length === 14) {
      setSearchingCnpj(true);
      try {
        const data = await cnpjService.search(cleanCnpj);
        if (data) {
          setForm(prev => ({
            ...prev,
            companyName: data.razao_social || prev.companyName,
            street: data.logradouro || prev.street,
            number: data.numero || prev.number,
            complement: data.complemento || prev.complement,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.municipio || prev.city,
            state: data.uf || prev.state,
            zipCode: data.cep || prev.zipCode,
            phone: (data.ddd_telefone_1?.split('/')[0] || '').trim().replace(/\D/g, '').slice(0, 11) || prev.phone,
            // Se não tiver nome ainda, sugerir o nome fantasia
            name: prev.name || data.nome_fantasia || data.razao_social || '',
          }));
        }
      } catch (e) {
        console.error('Erro ao buscar CNPJ:', e);
      }
      setSearchingCnpj(false);
    }
  };

  // A regra de quem pertence ao grupo e de quanto ele fatura mora em
  // `utils/grupoEconomico.ts`, com teste sobre os números de produção. Aqui só
  // se desenha. Até 01/10/2026 o card somava só os contratos da matriz e dizia
  // R$ 79,98 para a PARANAFRIGOR, que fatura R$ 685,30.
  const grupos = useMemo(() => montarGrupos(clients, locations, contracts), [clients, locations, contracts]);
  const membrosIds = useMemo(() => idsDeMembros(grupos), [grupos]);
  const idsDeMatriz = useMemo(() => new Set(grupos.map((g) => g.matriz.id)), [grupos]);

  // Grupos SEMPRE listados; a busca filtra por matriz ou por qualquer empresa
  // do grupo (digitar TOTALFRIGOR acha o grupo PARANAFRIGOR).
  const gruposVisiveis = useMemo(
    () => grupos
      .filter((g) => grupoCasaBusca(search, g))
      .sort((a, b) => b.mrrTotal - a.mrrTotal),
    [grupos, search],
  );
  // Clientes fora de qualquer grupo: é aqui que se acha alguém para criar o
  // PRIMEIRO vínculo. Só com busca, para não despejar a base inteira.
  const outrosClientes = useMemo(() => {
    if (!search.trim()) return [];
    return clients
      .filter((c) => !idsDeMatriz.has(c.id) && !membrosIds.has(c.id) && casaBusca(search, c))
      .slice(0, 30);
  }, [clients, idsDeMatriz, membrosIds, search]);

  // Cards: tudo no MESMO escopo, o dos grupos. "Total de Clientes 199" ao lado
  // de "Clientes com Filiais 2" parecia dizer que faltavam 197 na lista.
  const totalEmpresas = grupos.reduce((s, g) => s + g.membros.length, 0);
  const mrrConsolidado = grupos.reduce((s, g) => s + g.mrrTotal, 0);
  const totalProblemas = grupos.reduce((s, g) => s + g.problemas, 0);

  const nomeDoCliente = (c: Client | GClient) => c.name || c.tradeName || c.companyName || `Cliente #${c.id}`;
  const nomePorId = (id: number) => { const c = clients.find((x) => x.id === id); return c ? nomeDoCliente(c) : `Cliente #${id}`; };

  // Quem pode virar filial da matriz aberta: não é ela, não tem matriz, não é matriz de ninguém e não está em grupo
  // nenhum (nem como espelho de local). Filial de filial não existe: o grupo tem um nível só.
  const candidatosAFilial = useMemo(() => {
    if (!selectedClient) return [];
    return clients
      .filter((c) => c.id !== selectedClient.id && c.parentClientId == null && !idsDeMatriz.has(c.id) && !membrosIds.has(c.id))
      .sort((a, b) => nomeDoCliente(a).localeCompare(nomeDoCliente(b), 'pt-BR'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, selectedClient, idsDeMatriz, membrosIds]);
  const candidatosVisiveis = candidatosAFilial.filter((c) => casaBusca(filtroFilial, c));
  const filialEscolhida = filialId === '' ? null : clients.find((c) => c.id === filialId) || null;
  // A matriz aberta já é filial de outro grupo: ganhar filial faria um neto.
  const matrizEhFilial = selectedClient?.parentClientId != null;
  // O CNPJ digitado no caminho do local já é de um cliente (ver `clienteComOCnpj`).
  const cnpjNovoNoForm = !editingLocation || soDigitos(form.cnpj) !== soDigitos(editingLocation.cnpj);
  const donoDoCnpj = passo === 'local' && cnpjNovoNoForm ? clienteComOCnpj(form.cnpj, clients, editingLocation?.id) : null;
  const donoPodeSerFilial = !!donoDoCnpj && candidatosAFilial.some((c) => c.id === donoDoCnpj.id);

  // Grava SÓ o vínculo: PUT /clients/:id com { parentClientId }. O backend faz Object.assign do que vier, então o
  // cadastro da empresa (e-mail financeiro, vencimento, endereço) não é tocado.
  const vincularFilial = async () => {
    if (!selectedClient || !filialEscolhida) return;
    const nomeFilial = nomeDoCliente(filialEscolhida);
    const nomeMatriz = nomeDoCliente(selectedClient);
    if (!(await confirm({
      title: `Vincular ${nomeFilial} como filial de ${nomeMatriz}?`,
      confirmLabel: 'Vincular',
      consequence: `${nomeFilial} passa a aparecer no grupo ${nomeMatriz}, com a fatura própria dela. O cadastro dela não muda: só o vínculo.`,
    }))) return;
    setSaving(true);
    try {
      await clientsService.update(filialEscolhida.id, { parentClientId: selectedClient.id });
      setShowModal(false);
      aviso(`${nomeFilial} vinculada ao grupo ${nomeMatriz}.`, 'success');
      setExpandedClients((prev) => new Set(prev).add(selectedClient.id));
      await loadData();
    } catch (e) {
      aviso(`Não deu para vincular: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
    setSaving(false);
  };

  // Spinner de tela cheia: só a PRIMEIRA carga, sem nada para mostrar. Sem dados e com erro, a tela diz que não
  // carregou (e não "0 grupos", que afirmaria um banco vazio).
  const temDados = clients.length > 0;
  if (loading && !temDados) {
    return (
      <div className='flex items-center justify-center h-64' data-carga='carregando'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400'></div>
      </div>
    );
  }
  if (!temDados && carga.estado === 'erro') {
    return <EstadoDaLista carga={carga} temDados={false} oQue='os grupos' onTentarDeNovo={loadData} vazio={null}>{null}</EstadoDaLista>;
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-bold text-white'>Grupos Econômicos</h2>
          <p className='text-slate-400 text-sm'>A matriz e as empresas ligadas a ela, e quanto o grupo fatura somado</p>
        </div>
        {loading && <span className='text-xs text-slate-400' data-carga='atualizando'>Atualizando os grupos…</span>}
      </div>

      {/* Recarga que falhou: a lista que estava fica, e a faixa diz de quando ela é. */}
      {carga.estado === 'erro' && <EstadoDaLista carga={carga} temDados oQue='os grupos' onTentarDeNovo={loadData} vazio={null}>{null}</EstadoDaLista>}

      {/* Cards de estatísticas */}
      {/* Cartões do topo (fase 5 das listas): o CartaoDoTopo das outras listas. Não filtram, então não são botão; a pendência
          de "Vínculos com problema" é a barra de 3px no tom, como antes, e o número segue neutro. */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        <CartaoDoTopo rotulo='Grupos' valor={grupos.length} />
        <CartaoDoTopo rotulo='Empresas vinculadas' valor={totalEmpresas} />
        <CartaoDoTopo rotulo='MRR consolidado' valor={`R$ ${fmt(mrrConsolidado)}`} />
        <CartaoDoTopo
          rotulo='Vínculos com problema'
          valor={
            <span className='inline-flex items-center gap-1.5'>
              {totalProblemas}
              {totalProblemas > 0 && <span aria-hidden className={`text-base ${TONE_TEXT.warning}`}>⚠</span>}
            </span>
          }
          barra={totalProblemas > 0 ? 'warning' : null}
          data-cartao-problemas
        />
      </div>

      {/* Busca */}
      <ListToolbar search={<ToolbarSearch value={search} onChange={setSearch} placeholder='Buscar cliente por nome ou CNPJ...' />} />

      {/* Lista de grupos */}
      <div className='space-y-3'>
        {gruposVisiveis.length === 0 ? (
          <div className='text-center py-8 text-slate-500'>
            {search ? 'Nenhum grupo com esse nome ou CNPJ' : 'Nenhum grupo cadastrado'}
          </div>
        ) : (
          gruposVisiveis.map(grupo => {
            const client = grupo.matriz as Client;
            const isExpanded = expandedClients.has(client.id);

            return (
              <div key={client.id} id={`grupo-${client.id}`} className='scroll-mt-4'>
              <CascaDaLista>
                {/* Header do grupo */}
                <div
                  className='p-4 flex items-center justify-between cursor-pointer hover:bg-central-elevado'
                  onClick={() => toggleClient(client.id)}
                >
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-central-elevado rounded-lg'>
                      <Icon name='building' className='w-5 h-5 text-central-texto-fraco' />
                    </div>
                    <div>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <h3 className='font-semibold text-white'>{nomeDoCliente(client)}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${TONE_BADGE.group}`}>
                          {grupo.membros.length} {grupo.membros.length === 1 ? 'empresa' : 'empresas'}
                        </span>
                        {grupo.problemas > 0 && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${TONE_BADGE.warning}`}>
                            ⚠ {grupo.problemas} {grupo.problemas === 1 ? 'vínculo não resolve' : 'vínculos não resolvem'}
                          </span>
                        )}
                      </div>
                      {/* C1: UMA sub-linha, razão · CNPJ (a peça partesDaSubLinha, a mesma da CelulaCliente). */}
                      <p className='text-sm text-slate-400' data-sub-linha>{partesDaSubLinha({ razao: client.companyName, cnpj: client.cnpj }).join(' · ')}</p>
                    </div>
                  </div>
                  <div className='flex items-center gap-4'>
                    <div className='text-right'>
                      <p className='text-lg font-bold text-white'>R$ {fmt(grupo.mrrTotal)}</p>
                      <p className='text-xs text-slate-500'>MRR do grupo</p>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); openAddLocation(client); }}
                      className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                      title='Vincular empresa ao grupo'
                    >
                      <Icon name='plus' className='w-5 h-5' />
                    </button>
                    <Icon
                      name={isExpanded ? 'chevronDown' : 'chevronRight'}
                      className='w-5 h-5 text-slate-500'
                    />
                  </div>
                </div>

                {/* Empresas do grupo */}
                {isExpanded && (
                  <div className='border-t border-central-borda'>
                    {/* O rateio do grupo: UMA linha de resumo e o botão "Rateio", que abre a gaveta com Divisão, Motor,
                        Listas e Histórico (RateioDoGrupoExpandido, 10/10/2026). Sem rateio, o "Começar a dividir". */}
                    <RateioDoGrupo
                      clientId={client.id} contracts={contracts} clients={clients} nome={nomePorId} versao={versaoDoRateio} ehAdmin={ehAdmin}
                      abrirAplicarAoLer={aplicarAoLer === client.id}
                      onRedividir={() => setRedividindo(client.id)}
                      onListasGravadas={() => { setVersaoDoRateio((v) => v + 1); aviso('Divisão nova gravada.', 'success'); }}
                      onAplicado={() => { setAplicarAoLer(null); loadData(); setVersaoDoRateio((v) => v + 1); }}
                      onPrimeiraDivisaoGravada={() => { setAplicarAoLer(client.id); setVersaoDoRateio((v) => v + 1); aviso('Primeira divisão gravada. O motor segue desligado.', 'success'); }}
                    />
                    {/* Matriz */}
                    <div className='p-4 pl-12 border-b border-central-borda flex items-center justify-between'>
                      <div className='flex items-center gap-3'>
                        <div className='p-2 bg-central-elevado rounded-lg'>
                          <Icon name='building' className='w-4 h-4 text-central-texto-fraco' />
                        </div>
                        <div>
                          <div className='flex items-center gap-2'>
                            <span className={`px-2 py-0.5 text-xs font-mono rounded ${TONE_BADGE.group}`}>
                              MATRIZ
                            </span>
                            <span className='font-medium text-white'>{nomeDoCliente(client)}</span>
                          </div>
                          <p className='text-sm text-slate-500' data-sub-linha>{partesDaSubLinha({ razao: client.companyName, cnpj: client.cnpj }).join(' · ')}</p>
                        </div>
                      </div>
                      <div className='flex items-center gap-4'>
                        <div className='text-right'>
                          <p className='text-lg font-bold text-white'>R$ {fmt(grupo.mrrDaMatriz)}</p>
                          <p className='text-xs text-slate-500'>a própria matriz</p>
                        </div>
                        {/* O lápis abre o CADASTRO CANÔNICO do cliente (o modal da Lista, que relê o servidor). Até
                            09/10/2026 abria um "Editar Matriz" próprio: um segundo formulário do cadastro, montado da
                            foto da lista e salvo por PUT com os ~50 campos dela, que desfazia calado o que a Lista
                            tivesse gravado depois da carga desta tela. */}
                        {onEditarCliente && (
                          <div className='flex items-center gap-1'>
                            <button
                              onClick={() => onEditarCliente(client)}
                              className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                              title='Editar o cadastro do cliente'
                            >
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='divide-y divide-slate-800'>
                      {[...grupo.membros, ...(inativosVisiveis.has(client.id) ? grupo.membrosInativos : [])].map(m => {
                        const inativo = membroInativo(m);
                        const location = m.location as ClientLocation | null;
                        const nome = location?.name || (m.cliente ? nomeDoCliente(m.cliente) : '—');
                        const cnpj = location?.cnpj || m.cliente?.cnpj || '';
                        const mrr = m.mrrNaMatriz + m.mrrProprio;
                        return (
                          <div
                            key={location ? `l${location.id}` : `c${m.cliente?.id}`}
                            className={`p-4 pl-12 hover:bg-central-elevado flex items-center justify-between gap-4 ${inativo ? 'opacity-60' : ''}`}
                          >
                            <div className='flex items-center gap-3 min-w-0'>
                              <div className={`p-2 rounded-lg shrink-0 ${TONE_SURFACE.group}`}>
                                <Icon name='mapPin' className={`w-4 h-4 ${TONE_TEXT.group}`} />
                              </div>
                              <div className='min-w-0'>
                                <div className='flex items-center gap-2 flex-wrap'>
                                  {location?.code && (
                                    <span className='px-2 py-0.5 text-xs font-mono bg-slate-700 text-slate-300 rounded'>
                                      {location.code}
                                    </span>
                                  )}
                                  <span className='font-medium text-white'>{nome}</span>
                                  {m.vinculo && (
                                    <span
                                      className={`px-2 py-0.5 text-xs rounded-full ${TONE_BADGE.group}`}
                                      title={m.vinculo === 'filial'
                                        ? 'Mesma raiz de CNPJ da matriz: estabelecimento da mesma empresa'
                                        : 'Raiz de CNPJ diferente da matriz: outra empresa do mesmo grupo'}
                                    >
                                      {m.vinculo}
                                    </span>
                                  )}
                                  {inativo && (
                                    <span
                                      className={`px-2 py-0.5 text-xs rounded-full ${TONE_BADGE.neutral}`}
                                      title='O cliente desta empresa está Inativo. O vínculo fica guardado: só sai da listagem e da contagem.'
                                    >
                                      cliente inativo
                                    </span>
                                  )}
                                  {location && !location.isActive && (
                                    <span className={`px-2 py-0.5 text-xs rounded-full ${TONE_BADGE.danger}`}>
                                      Inativa
                                    </span>
                                  )}
                                </div>
                                {/* C1: uma sub-linha, CNPJ · cidade (e a origem do vínculo, quando é pelo cadastro). */}
                                <p className='text-sm text-slate-500' data-sub-linha>
                                  {[
                                    ...(cnpj ? partesDaSubLinha({ cnpj }) : ['CNPJ não informado']),
                                    location?.city ? `${location.city}/${location.state}` : null,
                                    m.origem === 'cliente' ? 'ligado pelo cadastro do cliente' : null,
                                  ].filter(Boolean).join(' · ')}
                                </p>
                                {m.vinculoQuebrado && (
                                  <p className={`text-xs mt-1 ${TONE_TEXT.warning}`}>
                                    ⚠ O vínculo não resolve: nenhum item da matriz aponta para esta empresa e nenhum cliente tem este CNPJ. O R$ 0,00 aqui não quer dizer que ela não consome.
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className='flex items-center gap-4 shrink-0'>
                              <div className='text-right'>
                                <p className={`text-lg font-bold ${m.vinculoQuebrado ? TONE_TEXT.warning : 'text-central-texto'}`}>
                                  {m.vinculoQuebrado ? '?' : `R$ ${fmt(mrr)}`}
                                </p>
                                <p className='text-xs text-slate-500'>{m.vinculoQuebrado ? 'vínculo quebrado' : textoDeCobranca(m)}</p>
                              </div>
                              {/* Filial ligada pelo cadastro (parentClientId, sem client_locations): o mesmo lápis da matriz, que abre
                                  o cadastro canônico pelo id (10/10/2026). Até aqui a linha não tinha ação nenhuma. */}
                              {!location && m.origem === 'cliente' && m.cliente && onEditarCliente && (
                                <button
                                  onClick={() => onEditarCliente(m.cliente!)}
                                  className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                                  title='Editar o cadastro do cliente'
                                >
                                  <Icon name='edit' className='w-4 h-4' />
                                </button>
                              )}
                              {location && (
                                <div className='flex items-center gap-1'>
                                  <button
                                    onClick={() => openEditLocation(location)}
                                    className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                                    title='Editar o endereço'
                                  >
                                    <Icon name='edit' className='w-4 h-4' />
                                  </button>
                                  {/* A filial com endereço que TAMBÉM tem cadastro de cliente (as lojas FESTVAL): os dois lápis, com
                                      títulos distintos (decisão D1 do Maestro, 10/10/2026). */}
                                  {m.cliente && onEditarCliente && (
                                    <button
                                      onClick={() => onEditarCliente(m.cliente!)}
                                      className='p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                                      title='Editar o cadastro do cliente'
                                    >
                                      <Icon name='building' className='w-4 h-4' />
                                    </button>
                                  )}
                                  {/* D2 (o padrão de toda lista): a lixeira saiu da linha; "Remover do grupo" mora no ⋯, em perigo
                                      (o deleteLocation já pergunta antes). */}
                                  <MaisAcoes
                                    title={`Mais ações de ${nome}`}
                                    ancoragem='fixa'
                                    itens={[{ label: 'Remover do grupo', onClick: () => deleteLocation(location), danger: true }]}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {grupo.membrosInativos.length > 0 && (
                      <div className='px-4 py-2 pl-12 border-t border-central-borda'>
                        <button
                          onClick={() => toggleInativos(client.id)}
                          className='text-xs text-slate-500 hover:text-slate-300'
                        >
                          {inativosVisiveis.has(client.id)
                            ? 'Ocultar empresas inativas'
                            : `Ver empresas inativas (${grupo.membrosInativos.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </CascaDaLista>
              </div>
            );
          })
        )}
      </div>

      {/* Outros clientes: onde se acha alguém para criar o primeiro vínculo. */}
      <div className='space-y-2'>
        <h3 className='text-sm font-medium text-slate-300'>Outros clientes</h3>
        {!search.trim() ? (
          <p className='text-sm text-slate-500'>Busque um cliente pelo nome ou CNPJ para começar um grupo novo a partir dele.</p>
        ) : outrosClientes.length === 0 ? (
          <p className='text-sm text-slate-500'>Nenhum cliente fora de grupo com esse nome ou CNPJ.</p>
        ) : (
          <CascaDaLista className='divide-y divide-slate-800'>
            {outrosClientes.map(c => (
              <div key={c.id} className='px-4 py-3 flex items-center justify-between gap-4'>
                <div className='min-w-0'>
                  <p className='text-white font-medium truncate'>{nomeDoCliente(c)}</p>
                  <p className='text-sm text-slate-500 truncate' data-sub-linha>{(c.cnpj || c.companyName) ? partesDaSubLinha({ razao: c.companyName, cnpj: c.cnpj }).join(' · ') : 'CNPJ não informado'}</p>
                </div>
                <button
                  onClick={() => openAddLocation(c)}
                  className='shrink-0 text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-700'
                >
                  + Vincular empresa
                </button>
              </div>
            ))}
          </CascaDaLista>
        )}
      </div>

      {/* Redividir: a base mudou e o admin grava a divisão calculada. Depois de
          gravar, o painel do grupo é remontado para ler a versão nova. */}
      {redividindo != null && (
        <RedividirDoGrupo
          clientId={redividindo}
          nome={nomePorId}
          onClose={() => setRedividindo(null)}
          onGravado={() => { setRedividindo(null); setVersaoDoRateio((v) => v + 1); aviso('Divisão nova gravada.', 'success'); }}
        />
      )}

      {/* O modal era max-w-lg, centralizado com items-center e rolagem interna:
          mais alto que a tela, o topo vazava e o cabeçalho cobria Código e Nome.
          Agora: largo (max-w-5xl, mesma régua da conciliação do BankAccountsTab),
          cabeçalho e rodapé fixos, rolagem só no corpo, seções em duas colunas. */}
      {/* Formulário digitado: antes fechava ao clicar fora (onMouseDown no fundo);
          agora só pelo × ou Cancelar, para não perder o que foi digitado. */}
      {showModal && (
        <CascaDeModal
          largura='xl'
          titulo={editingLocation ? 'Editar empresa do grupo' : passo === 'cliente' ? 'Vincular cliente como filial' : passo === 'local' ? 'Nova empresa no grupo' : 'Vincular empresa ao grupo'}
          subtitulo={selectedClient ? `Grupo ${selectedClient.name || selectedClient.tradeName || selectedClient.companyName}` : undefined}
          onClose={() => setShowModal(false)}
          corpoClassName='px-6 py-5'
          rodape={
            <div className='ml-auto flex gap-3'>
              {!editingLocation && passo !== 'escolher' && (
                <button
                  onClick={() => setPasso('escolher')}
                  className='px-4 py-2 text-slate-400 hover:text-slate-200 rounded-lg'
                >
                  Voltar
                </button>
              )}
              <button
                onClick={() => setShowModal(false)}
                className='px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800'
              >
                Cancelar
              </button>
              {passo === 'cliente' && (
                <button
                  onClick={vincularFilial}
                  disabled={saving || !filialEscolhida || matrizEhFilial}
                  className='px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong disabled:opacity-50'
                >
                  {saving ? 'Vinculando...' : 'Vincular como filial'}
                </button>
              )}
              {passo === 'local' && (
                <button
                  onClick={saveLocation}
                  disabled={saving || !!donoDoCnpj}
                  className='px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong disabled:opacity-50'
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              )}
            </div>
          }
        >
              {passo === 'escolher' && (
                <div className='space-y-4'>
                  <p className='text-sm text-slate-400'>
                    Filial é um cliente com cadastro e fatura próprios, que pode ter cota no rateio do grupo. Local é um endereço ou CNPJ dentro da conta da matriz, cobrado na fatura dela.
                  </p>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                    <button
                      onClick={() => setPasso('cliente')}
                      className='text-left p-4 rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50'
                    >
                      <p className='font-medium text-white'>Cliente já cadastrado</p>
                      <p className='text-sm text-slate-400 mt-1'>Vincular como filial. O cadastro e a fatura dele continuam como estão.</p>
                    </button>
                    <button
                      onClick={() => setPasso('local')}
                      className='text-left p-4 rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50'
                    >
                      <p className='font-medium text-white'>Nova empresa</p>
                      <p className='text-sm text-slate-400 mt-1'>Local da matriz: código, nome, CNPJ e endereço, cobrado na fatura da matriz.</p>
                    </button>
                  </div>
                </div>
              )}

              {passo === 'cliente' && (
                <div className='space-y-4'>
                  <p className='text-sm text-slate-400'>
                    O cliente escolhido passa a ser filial de {selectedClient ? nomeDoCliente(selectedClient) : 'a matriz'}. Só o vínculo é gravado: e-mail financeiro, vencimento e endereço dele não mudam.
                  </p>
                  {matrizEhFilial ? (
                    <p role='alert' className={`px-4 py-3 rounded-lg border text-sm ${TONE_BORDER.warning} ${TONE_BADGE.warning}`}>
                      {selectedClient ? nomeDoCliente(selectedClient) : 'Esta empresa'} já é filial de {nomePorId(selectedClient!.parentClientId as number)}. O grupo tem um nível só: vincule a empresa à matriz de lá.
                    </p>
                  ) : (
                    <EstadoDaLista
                      carga={carga}
                      temDados={clients.length > 0}
                      oQue='os clientes'
                      onTentarDeNovo={loadData}
                      vazio={<p className='text-sm text-slate-500'>Nenhum cliente para vincular: todos já estão em algum grupo.</p>}
                    >
                      {candidatosAFilial.length === 0 ? (
                        <p className='text-sm text-slate-500'>Nenhum cliente para vincular: todos já estão em algum grupo, são matriz de um ou já têm matriz.</p>
                      ) : (
                        <div className='space-y-3'>
                          <div>
                            <label className={ROTULO}>Buscar por nome ou CNPJ</label>
                            <input
                              type='text'
                              value={filtroFilial}
                              onChange={e => setFiltroFilial(e.target.value)}
                              placeholder='Ex: FISCALMAX'
                              className={CAMPO}
                            />
                          </div>
                          <div>
                            <label className={ROTULO}>Cliente *</label>
                            <select
                              value={filialId}
                              onChange={e => setFilialId(e.target.value ? Number(e.target.value) : '')}
                              className={CAMPO}
                            >
                              <option value=''>{candidatosVisiveis.length ? 'Selecione o cliente' : 'Nenhum cliente com essa busca'}</option>
                              {opcaoAtualForaDaLista(candidatosVisiveis, filialId, filialEscolhida ? nomeDoCliente(filialEscolhida) : `Cliente #${filialId}`, carga.estado === 'erro')}
                              {candidatosVisiveis.map(c => (
                                <option key={c.id} value={c.id}>
                                  {nomeDoCliente(c)}{c.cnpj ? ` · ${fmtCNPJ(c.cnpj)}` : ''}{c.status && c.status !== 'Ativo' ? ' · inativo' : ''}
                                </option>
                              ))}
                            </select>
                            <p className='text-xs text-slate-500 mt-1'>
                              {candidatosAFilial.length} cliente(s) sem grupo. Quem já é matriz, já tem matriz ou já está em um grupo não aparece.
                            </p>
                          </div>
                        </div>
                      )}
                    </EstadoDaLista>
                  )}
                </div>
              )}

              {passo === 'local' && (
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6'>
                {/* Coluna 1: quem é a empresa e onde fica */}
                <div className='space-y-6 min-w-0'>
                  <section>
                    <p className={SECAO}>Identificação</p>
                    <div className='grid grid-cols-4 gap-3'>
                      <div>
                        <label className={ROTULO}>Código *</label>
                        <input
                          type='text'
                          value={form.code || ''}
                          onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                          placeholder='L01'
                          className={CAMPO}
                        />
                      </div>
                      <div className='col-span-3'>
                        <label className={ROTULO}>Nome da empresa *</label>
                        <input
                          type='text'
                          value={form.name || ''}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          placeholder='Ex: Loja Centro, TOTALFRIGOR'
                          className={CAMPO}
                        />
                      </div>
                      <div className='col-span-4 sm:col-span-2'>
                        <label className={ROTULO}>CNPJ</label>
                        <div className='relative'>
                          <input
                            type='text'
                            value={form.cnpj || ''}
                            onChange={e => handleCnpjChange(e.target.value)}
                            placeholder='00.000.000/0000-00'
                            className={CAMPO}
                          />
                          {searchingCnpj && (
                            <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                              <div className='animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent'></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col-span-4 sm:col-span-2'>
                        <label className={ROTULO}>Razão Social</label>
                        <input
                          type='text'
                          value={form.companyName || ''}
                          onChange={e => setForm({ ...form, companyName: e.target.value })}
                          placeholder='Razão social da empresa'
                          className={CAMPO}
                        />
                      </div>
                      {donoDoCnpj && (
                        <div role='alert' className={`col-span-4 px-4 py-3 rounded-lg border text-sm ${TONE_BORDER.warning} ${TONE_BADGE.warning}`}>
                          <p>
                            Este CNPJ já é do cliente <strong>{nomeDoCliente(donoDoCnpj)}</strong>. Salvar como local reescreveria o cadastro dele (nome, endereço, e-mail financeiro e vencimento) com os deste formulário e os da matriz.
                          </p>
                          {donoPodeSerFilial ? (
                            <button
                              onClick={() => { setFilialId(donoDoCnpj.id); setFiltroFilial(''); setPasso('cliente'); }}
                              className='mt-2 underline'
                            >
                              Vincular {nomeDoCliente(donoDoCnpj)} como filial
                            </button>
                          ) : (
                            <p className='mt-1'>Ele não pode virar filial deste grupo: já é matriz, já tem matriz ou já está em um grupo.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </section>

                  <section>
                    <p className={SECAO}>Endereço</p>
                    <div className='grid grid-cols-6 gap-3'>
                      <div className='col-span-2'>
                        <label className={ROTULO}>CEP</label>
                        <input type='text' value={form.zipCode || ''} onChange={e => setForm({ ...form, zipCode: e.target.value })} placeholder='00000-000' className={CAMPO} />
                      </div>
                      <div className='col-span-4'>
                        <label className={ROTULO}>Rua</label>
                        <input type='text' value={form.street || ''} onChange={e => setForm({ ...form, street: e.target.value })} placeholder='Rua/Avenida' className={CAMPO} />
                      </div>
                      <div className='col-span-2'>
                        <label className={ROTULO}>Número</label>
                        <input type='text' value={form.number || ''} onChange={e => setForm({ ...form, number: e.target.value })} placeholder='Nº' className={CAMPO} />
                      </div>
                      <div className='col-span-4'>
                        <label className={ROTULO}>Complemento</label>
                        <input type='text' value={form.complement || ''} onChange={e => setForm({ ...form, complement: e.target.value })} placeholder='Sala, Andar' className={CAMPO} />
                      </div>
                      <div className='col-span-6 sm:col-span-2'>
                        <label className={ROTULO}>Bairro</label>
                        <input type='text' value={form.neighborhood || ''} onChange={e => setForm({ ...form, neighborhood: e.target.value })} placeholder='Bairro' className={CAMPO} />
                      </div>
                      <div className='col-span-4 sm:col-span-3'>
                        <label className={ROTULO}>Cidade</label>
                        <input type='text' value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} placeholder='Cidade' className={CAMPO} />
                      </div>
                      <div className='col-span-2 sm:col-span-1'>
                        <label className={ROTULO}>UF</label>
                        <input type='text' value={form.state || ''} onChange={e => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder='PR' maxLength={2} className={CAMPO} />
                      </div>
                    </div>
                  </section>
                </div>

                {/* Coluna 2: com quem se fala e como se cobra */}
                <div className='space-y-6 min-w-0'>
                  <section>
                    <p className={SECAO}>Contato financeiro</p>
                    <div className='grid grid-cols-6 gap-3'>
                      <div className='col-span-6'>
                        <label className={ROTULO}>E-mail financeiro</label>
                        <input type='email' value={form.emailFinancial || ''} onChange={e => setForm({ ...form, emailFinancial: e.target.value })} placeholder='financeiro@empresa.com' className={CAMPO} />
                      </div>
                      <div className='col-span-4'>
                        <label className={ROTULO}>Telefone</label>
                        <input type='text' value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder='(00) 00000-0000' className={CAMPO} />
                      </div>
                      <div className='col-span-2'>
                        <label className={ROTULO}>Dia de vencimento</label>
                        <input
                          type='number'
                          value={form.dueDay || 10}
                          onChange={e => setForm({ ...form, dueDay: parseInt(e.target.value) || 10 })}
                          min={1}
                          max={31}
                          className={CAMPO}
                        />
                      </div>
                    </div>
                  </section>

                  <section>
                    <p className={SECAO}>Contato responsável</p>
                    <div className='grid grid-cols-6 gap-3'>
                      <div className='col-span-6'>
                        <label className={ROTULO}>Nome</label>
                        <input type='text' value={form.contactName || ''} onChange={e => setForm({ ...form, contactName: e.target.value })} placeholder='Nome do contato' className={CAMPO} />
                      </div>
                      <div className='col-span-6 sm:col-span-4'>
                        <label className={ROTULO}>E-mail</label>
                        <input type='email' value={form.contactEmail || ''} onChange={e => setForm({ ...form, contactEmail: e.target.value })} placeholder='email@empresa.com' className={CAMPO} />
                      </div>
                      <div className='col-span-6 sm:col-span-2'>
                        <label className={ROTULO}>Telefone</label>
                        <input type='text' value={form.contactPhone || ''} onChange={e => setForm({ ...form, contactPhone: e.target.value })} placeholder='(00) 00000-0000' className={CAMPO} />
                      </div>
                    </div>
                  </section>

                  <section>
                    <p className={SECAO}>Observações</p>
                    <textarea
                      value={form.notes || ''}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      rows={3}
                      className={`${CAMPO} resize-none`}
                    />
                    <label htmlFor='isActive' className='flex items-center gap-2 mt-3 text-sm text-slate-300 cursor-pointer'>
                      <input
                        type='checkbox'
                        id='isActive'
                        checked={form.isActive !== false}
                        onChange={e => setForm({ ...form, isActive: e.target.checked })}
                        className='w-4 h-4 rounded border-slate-600 bg-slate-800 text-primary focus:ring-primary'
                      />
                      Empresa ativa no grupo
                    </label>
                  </section>
                </div>
              </div>
              )}
        </CascaDeModal>
      )}
    </div>
  );
};

export default ClientLocationsManager;
